// Espaces "invisibles" produits par Intl pour les nombres FR :
//   U+00A0 = NBSP, U+202F = NNBSP (espace fine insécable, Node 18+),
//   U+2009 = espace fine. On les remplace par un espace normal pour
//   rester cohérent (UI, exports CSV, copier-coller).
const SPACE_VARIANTS = new RegExp("[   ]", "g");

export function formatEUR(
  value: number,
  options: { cents?: boolean } = {},
): string {
  // Garde-fou : NaN / Infinity ne doivent jamais apparaître dans l'UI ou
  // un email envoyé. Cf. relances groupe avec invoice.amount_due null.
  const safe = Number.isFinite(value) ? value : 0;
  const formatted = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: options.cents === false ? 0 : 2,
    maximumFractionDigits: options.cents === false ? 0 : 2,
  }).format(safe);
  return formatted.replace(SPACE_VARIANTS, " ");
}

export type AgeBucket = {
  key: "0-30" | "31-60" | "61-90" | ">90";
  label: string;
  /** Fond + texte d'un badge de la tranche (rôles age-* de tokens.css). */
  badgeClass: string;
  /** Fond d'une pastille de la tranche. */
  dotClass: string;
};

export function ageBucket(days: number): AgeBucket {
  if (days <= 30) {
    return {
      key: "0-30",
      label: "0–30j",
      badgeClass: "bg-age-0-30-bg text-age-0-30-fg",
      dotClass: "bg-age-0-30-fg",
    };
  }
  if (days <= 60) {
    return {
      key: "31-60",
      label: "31–60j",
      badgeClass: "bg-age-31-60-bg text-age-31-60-fg",
      dotClass: "bg-age-31-60-fg",
    };
  }
  if (days <= 90) {
    return {
      key: "61-90",
      label: "61–90j",
      badgeClass: "bg-age-61-90-bg text-age-61-90-fg",
      dotClass: "bg-age-61-90-fg",
    };
  }
  return {
    key: ">90",
    label: ">90j",
    badgeClass: "bg-age-90-bg text-age-90-fg",
    dotClass: "bg-age-90-fg",
  };
}

export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function fmtDateLong(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Date et heure d'un évènement, lisibles telles quelles : « 12/09/2026 à 14:32 ».
 *
 * Préférée à `relTime` pour les emails et contacts (retours compta du
 * 2026-09-25) : « il y a 12 j » oblige à recompter pour savoir QUAND une
 * relance est partie, alors qu'on la cite au client.
 */
export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${fmtDate(value)} à ${time}`;
}

export function relTime(value: string | null | undefined): string {
  if (!value) return "—";
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.floor((Date.now() - t) / 1000);
  if (diffSec < 60) return "à l'instant";
  if (diffSec < 3600) return `il y a ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `il y a ${Math.floor(diffSec / 3600)} h`;
  const days = Math.floor(diffSec / 86400);
  if (days < 30) return `il y a ${days} j`;
  const months = Math.floor(days / 30);
  if (months < 12) return `il y a ${months} mois`;
  const years = Math.floor(months / 12);
  return `il y a ${years} an${years > 1 ? "s" : ""}`;
}
